import { useEffect, useState, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/react";
import { trTR } from "@clerk/localizations";

const ClerkAvailableContext = createContext(false);
export const useClerkAvailable = () => useContext(ClerkAvailableContext);

const localization = {
  ...trTR,
  signIn: { ...trTR.signIn, start: { ...trTR.signIn?.start, title: "Gazi Sepet'e giriş yapın", titleCombined: "Gazi Sepet'e giriş yapın", subtitle: "Devam etmek için giriş yapın", subtitleCombined: "Devam etmek için giriş yapın" } },
  signUp: { ...trTR.signUp, start: { ...trTR.signUp?.start, title: "Gazi Sepet hesabı oluşturun", titleCombined: "Gazi Sepet hesabı oluşturun", subtitle: "İşletmenizi hemen kaydedin", subtitleCombined: "İşletmenizi hemen kaydedin" } },
};

// Brand Clerk here once: it styles sign-in, sign-up and the user menu. Dark app: `yarn add @clerk/ui@1.38.1`,
// `import { dark } from "@clerk/ui/themes"`, then add `theme: dark, captcha: { theme: "dark" }` (see Styling).
const appearance = {
  variables: {
    colorPrimary: "#FF6B0A",
    colorForeground: "#0F1F45",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    borderRadius: "0.75rem",
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Starts Clerk from the backend's runtime config, so deploy's live keys need no rebuild.
// Must render inside <BrowserRouter> (see step 3) so Clerk navigates without reloading.
export default function ClerkAuthProvider({ children }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  // Retries while the backend restarts (e.g. right after provisioning): the app's
  // Clerk components crash if they ever render without <ClerkProvider>.
  useEffect(() => {
    let cancelled = false;
    const load = async (attempt = 0) => {
      try {
        const cfg = await (await fetch(`${BACKEND_URL}/api/clerk/config`)).json();
        if (cfg.enabled) return cancelled || setConfig(cfg);
        // Backend answered "Clerk not configured": no point retrying, use local e-posta+şifre auth now.
        if (cfg && cfg.enabled === false) return cancelled || setUnavailable(true);
      } catch {}
      if (cancelled) return;
      if (attempt >= 10) return setUnavailable(true);
      setTimeout(() => load(attempt + 1), 1500);
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // No managed Clerk keys yet (preview without provisioning): degrade gracefully so the
  // public pages (landing, QR menu, courier/caller apps) render instead of a blank screen.
  // Protected pages route to /sign-in, which shows a notice until keys are provisioned.
  if (unavailable) return <ClerkAvailableContext.Provider value={false}>{children}</ClerkAvailableContext.Provider>;
  if (!config) return null;
  return (
    <ClerkProvider
      publishableKey={config.publishable_key}
      proxyUrl={config.proxy_url || undefined}
      appearance={appearance}
      localization={localization}
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      afterSignOutUrl="/"
    >
      <ClerkAvailableContext.Provider value={true}>{children}</ClerkAvailableContext.Provider>
    </ClerkProvider>
  );
}
